import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { TKemMessagePayload } from "../utils/types";

/** After loading older rows, scroll + layout can briefly bounce at the top; ignore re-trigger during this window. */
const paginationTopCooldownMs = 450;

/** Only auto-follow the latest row when the user is already near the bottom. */
const FOLLOW_SCROLL_THRESHOLD = 120;

/** Only load older history when the user has scrolled near the top. */
const PAGINATION_TOP_THRESHOLD = 80;

/** Max time to keep forcing bottom while first paint / images settle. */
const INITIAL_PIN_MAX_MS = 1500;

const getDistanceFromBottom = (el: HTMLElement): number =>
  el.scrollHeight - (el.scrollTop + el.clientHeight);

const isNearBottom = (el: HTMLElement): boolean =>
  getDistanceFromBottom(el) < FOLLOW_SCROLL_THRESHOLD;

const isNearTop = (el: HTMLElement): boolean =>
  el.scrollTop <= PAGINATION_TOP_THRESHOLD;

export interface UseKemChatScrollOptions {
  currentChat: TKemMessagePayload[];
  loadingBotChat: "sending" | "streaming" | undefined;
  kemIsTyping: string;
  isLoadingHistory: boolean;
  isLoadingMoreHistory: boolean;
  handleGetNextRecentHistory: () => Promise<number>;
  getCanPaginateAtTop: () => boolean;
  setChatContainerRef?: (el: HTMLElement | null) => void;
  coachScrollContainerRef?: RefObject<HTMLDivElement | null>;
  fallbackScrollToBottom: () => void;
}

export interface ScrollToBottomOptions {
  instant?: boolean;
}

export interface ScrollToBottomAfterUpdateOptions {
  /** Scroll on the next frame without waiting for currentChat.length to change. */
  immediate?: boolean;
}

export interface UseKemChatScrollResult {
  combinedChatRef: (el: HTMLDivElement | null) => void;
  onScroll: () => void;
  queueScrollToBottomAfterUpdate: (
    options?: ScrollToBottomAfterUpdateOptions,
  ) => void;
  scrollToBottom: (options?: ScrollToBottomOptions) => void;
  showScrollToBottom: boolean;
  handleKemBubbleImageLoad: (index: number) => void;
}

interface PendingPaginationRestore {
  prevScrollHeight: number;
  prevScrollTop: number;
  prevLength: number;
}

export function useKemChatScroll(
  options: UseKemChatScrollOptions,
): UseKemChatScrollResult {
  const {
    currentChat,
    loadingBotChat,
    kemIsTyping,
    isLoadingHistory,
    isLoadingMoreHistory,
    handleGetNextRecentHistory,
    getCanPaginateAtTop,
    setChatContainerRef,
    coachScrollContainerRef,
    fallbackScrollToBottom,
  } = options;

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const localChatElRef = useRef<HTMLDivElement | null>(null);
  const lastScrollTopRef = useRef(0);
  const isRestoringScrollRef = useRef(false);
  const paginationCooldownUntilRef = useRef(0);
  /** Blocks top-pagination until initial bottom pin finishes or user takes over. */
  const isInitialLoadingRef = useRef(true);
  /** Keep forcing scroll to bottom while history first paints / settles. */
  const isPinningInitialRef = useRef(true);
  const userHasScrolledUpRef = useRef(false);
  const forceScrollOnNextLengthChangeRef = useRef(false);
  const prevLoadingForFollowRef =
    useRef<UseKemChatScrollOptions["loadingBotChat"]>(loadingBotChat);
  const prevLastMessageLenRef = useRef(0);
  const pendingPaginationRestoreRef = useRef<PendingPaginationRestore | null>(
    null,
  );

  const getScrollEl = useCallback(
    (): HTMLElement | null => localChatElRef.current,
    [],
  );

  const endInitialPin = useCallback(() => {
    isPinningInitialRef.current = false;
    isInitialLoadingRef.current = false;
  }, []);

  const handleKemBubbleImageLoad = useCallback(
    (index: number) => {
      const isLastRow = index === currentChat.length - 1;
      if (!isLastRow || userHasScrolledUpRef.current) {
        return;
      }

      requestAnimationFrame(() => {
        const el = getScrollEl();
        if (!el || !isNearBottom(el)) {
          return;
        }

        el.scrollTop = el.scrollHeight + 100;
      });
    },
    [currentChat.length, getScrollEl],
  );

  const scrollToBottom = useCallback(
    (scrollOptions?: ScrollToBottomOptions) => {
      userHasScrolledUpRef.current = false;

      const el = getScrollEl();
      if (el && currentChat.length > 0) {
        const top = el.scrollHeight + 100;
        if (scrollOptions?.instant) {
          el.scrollTop = top;
        } else {
          el.scrollTo({ top, behavior: "smooth" });
        }
        lastScrollTopRef.current = el.scrollTop;
        setShowScrollToBottom(false);
        return;
      }

      if (coachScrollContainerRef) {
        if (el) {
          el.scrollTop = el.scrollHeight;
          lastScrollTopRef.current = el.scrollTop;
        }
        setShowScrollToBottom(false);
        return;
      }

      fallbackScrollToBottom();
    },
    [
      currentChat.length,
      fallbackScrollToBottom,
      coachScrollContainerRef,
      getScrollEl,
    ],
  );

  const queueScrollToBottomAfterUpdate = useCallback(
    (scrollOptions?: ScrollToBottomAfterUpdateOptions) => {
      forceScrollOnNextLengthChangeRef.current = true;
      userHasScrolledUpRef.current = false;

      if (scrollOptions?.immediate) {
        requestAnimationFrame(() => {
          scrollToBottom({ instant: true });
        });
      }
    },
    [scrollToBottom],
  );

  const loadOlderMessagesAtTop = useCallback(() => {
    if (
      isInitialLoadingRef.current ||
      isRestoringScrollRef.current ||
      isLoadingMoreHistory ||
      isLoadingHistory ||
      loadingBotChat === "streaming" ||
      loadingBotChat === "sending" ||
      Date.now() < paginationCooldownUntilRef.current ||
      !getCanPaginateAtTop()
    ) {
      return;
    }

    const el = getScrollEl();
    if (!el) return;

    isRestoringScrollRef.current = true;
    pendingPaginationRestoreRef.current = {
      prevScrollHeight: el.scrollHeight,
      prevScrollTop: el.scrollTop,
      prevLength: currentChat.length,
    };

    void (async () => {
      try {
        const addedCount = await handleGetNextRecentHistory();
        if (addedCount > 0) {
          // Scroll restore applied in useLayoutEffect after messages prepend.
          return;
        }
      } catch {
        /* ignore */
      }

      pendingPaginationRestoreRef.current = null;
      isRestoringScrollRef.current = false;
    })();
  }, [
    currentChat.length,
    getCanPaginateAtTop,
    getScrollEl,
    handleGetNextRecentHistory,
    isLoadingHistory,
    isLoadingMoreHistory,
    loadingBotChat,
  ]);

  useLayoutEffect(() => {
    const pending = pendingPaginationRestoreRef.current;
    if (!pending) return;
    // Wait until older messages are actually prepended (ignore loader-only re-renders).
    if (currentChat.length <= pending.prevLength) return;

    const el = getScrollEl();
    if (!el) return;

    const { prevScrollHeight, prevScrollTop } = pending;
    pendingPaginationRestoreRef.current = null;

    el.scrollTop = prevScrollTop + (el.scrollHeight - prevScrollHeight);
    lastScrollTopRef.current = el.scrollTop;
    isRestoringScrollRef.current = false;
    paginationCooldownUntilRef.current = Date.now() + paginationTopCooldownMs;
  }, [currentChat, getScrollEl]);

  // Pin to bottom as soon as history messages are in the DOM (before paint).
  useLayoutEffect(() => {
    if (!isPinningInitialRef.current || isLoadingHistory) {
      return;
    }

    if (currentChat.length === 0) {
      // Wait for the first history batch (esp. KEM IC where loading starts false).
      return;
    }

    const el = getScrollEl();
    if (!el) return;

    el.scrollTop = el.scrollHeight + 100;
    lastScrollTopRef.current = el.scrollTop;
    setShowScrollToBottom(false);
  }, [currentChat, isLoadingHistory, getScrollEl]);

  // Keep pinning briefly while layout/images settle, then unblock pagination.
  useEffect(() => {
    if (!isPinningInitialRef.current || isLoadingHistory) {
      return undefined;
    }

    if (currentChat.length === 0) {
      return undefined;
    }

    let rafId = 0;
    let lastHeight = 0;
    let stableFrames = 0;
    const startedAt = Date.now();

    const tick = () => {
      if (!isPinningInitialRef.current) return;

      const el = getScrollEl();
      if (!el) {
        rafId = requestAnimationFrame(tick);
        return;
      }

      el.scrollTop = el.scrollHeight + 100;
      lastScrollTopRef.current = el.scrollTop;

      const height = el.scrollHeight;
      if (height === lastHeight && isNearBottom(el)) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        lastHeight = height;
      }

      if (stableFrames >= 3 || Date.now() - startedAt >= INITIAL_PIN_MAX_MS) {
        endInitialPin();
        return;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isLoadingHistory, currentChat.length, getScrollEl, endInitialPin]);

  const onScroll = useCallback(() => {
    const el = getScrollEl();
    if (!el || isRestoringScrollRef.current) return;

    const prevScrollTop = lastScrollTopRef.current;
    const distanceFromBottom = getDistanceFromBottom(el);
    setShowScrollToBottom(distanceFromBottom > 60);

    const scrolledUp = el.scrollTop < prevScrollTop - 1;
    if (scrolledUp) {
      userHasScrolledUpRef.current = true;
      // User took over during initial pin — stop forcing bottom and unblock pagination.
      if (isPinningInitialRef.current) {
        endInitialPin();
      }
    } else if (distanceFromBottom > FOLLOW_SCROLL_THRESHOLD) {
      userHasScrolledUpRef.current = true;
      if (isPinningInitialRef.current) {
        endInitialPin();
      }
    } else if (isNearBottom(el)) {
      userHasScrolledUpRef.current = false;
    }

    lastScrollTopRef.current = el.scrollTop;

    // Load older history when near top. Require !isNearBottom so short chats
    // that fit on screen (scrollTop 0 but still at the "bottom") do not paginate.
    if (isNearTop(el) && !isNearBottom(el)) {
      loadOlderMessagesAtTop();
    }
  }, [endInitialPin, getScrollEl, loadOlderMessagesAtTop]);

  useEffect(() => {
    if (loadingBotChat !== "streaming" && loadingBotChat !== "sending") {
      return;
    }

    const el = getScrollEl();
    if (el && isNearBottom(el)) {
      userHasScrolledUpRef.current = false;
    }
  }, [loadingBotChat, getScrollEl]);

  const combinedChatRef = useCallback(
    (el: HTMLDivElement | null) => {
      localChatElRef.current = el;
      setChatContainerRef?.(el as unknown as HTMLElement | null);

      if (coachScrollContainerRef) {
        coachScrollContainerRef.current = el;
      }
    },
    [setChatContainerRef, coachScrollContainerRef],
  );

  useEffect(() => {
    if (isInitialLoadingRef.current) return;
    if (currentChat.length === 0) return;

    const shouldForce = forceScrollOnNextLengthChangeRef.current;
    const el = getScrollEl();

    if (!el) return;

    if (!shouldForce && !isNearBottom(el)) return;

    forceScrollOnNextLengthChangeRef.current = false;
    scrollToBottom({ instant: true });
  }, [currentChat.length, getScrollEl, scrollToBottom]);

  useEffect(() => {
    const prevLoading = prevLoadingForFollowRef.current;
    prevLoadingForFollowRef.current = loadingBotChat;

    if (
      prevLoading === "streaming" &&
      loadingBotChat === undefined &&
      currentChat.length > 0
    ) {
      const el = getScrollEl();
      if (el && !userHasScrolledUpRef.current && isNearBottom(el)) {
        scrollToBottom({ instant: true });
      }
    }
  }, [loadingBotChat, currentChat.length, getScrollEl, scrollToBottom]);

  useEffect(() => {
    const last = currentChat[currentChat.length - 1];
    const len = last?.message?.length ?? 0;

    if (isInitialLoadingRef.current) {
      prevLastMessageLenRef.current = len;
      return;
    }

    const grew = len > prevLastMessageLenRef.current;
    prevLastMessageLenRef.current = len;

    if (!grew) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = getScrollEl();
        if (!userHasScrolledUpRef.current && el && isNearBottom(el)) {
          scrollToBottom({ instant: true });
        }
      });
    });
  }, [currentChat, getScrollEl, scrollToBottom]);

  useEffect(() => {
    if (loadingBotChat !== "streaming") {
      return undefined;
    }

    if (currentChat.length === 0) return undefined;

    const el = getScrollEl();
    if (!el) return undefined;

    const isFollowingRef = { current: true };
    let rafId: number | null = null;

    const follow = () => {
      if (!isFollowingRef.current) return;

      const distanceFromBottom =
        el.scrollHeight - (el.scrollTop + el.clientHeight);
      const shouldFollow =
        !userHasScrolledUpRef.current &&
        distanceFromBottom < FOLLOW_SCROLL_THRESHOLD &&
        loadingBotChat === "streaming";

      if (shouldFollow) {
        el.scrollTop = el.scrollHeight;
      }

      rafId = requestAnimationFrame(follow);
    };

    rafId = requestAnimationFrame(follow);

    return () => {
      isFollowingRef.current = false;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [loadingBotChat, currentChat.length, getScrollEl]);

  useEffect(() => {
    if (
      kemIsTyping &&
      !userHasScrolledUpRef.current &&
      currentChat.length > 0
    ) {
      const el = getScrollEl();

      if (el) {
        const distanceFromBottom =
          el.scrollHeight - (el.scrollTop + el.clientHeight);
        if (distanceFromBottom < FOLLOW_SCROLL_THRESHOLD) {
          requestAnimationFrame(() => {
            const scrollEl = getScrollEl();
            if (scrollEl) {
              scrollEl.scrollTop = scrollEl.scrollHeight;
            }
          });
        }
      }
    }
  }, [kemIsTyping, currentChat.length, getScrollEl]);

  return {
    combinedChatRef,
    onScroll,
    queueScrollToBottomAfterUpdate,
    scrollToBottom,
    showScrollToBottom,
    handleKemBubbleImageLoad,
  };
}
